import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export interface ServiceMultiSelectOption {
  id: string;
  name: string;
}

interface ServiceMultiSelectProps {
  options: ServiceMultiSelectOption[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
}

export function ServiceMultiSelect({
  options,
  selectedIds,
  onChange,
  disabled = false,
}: ServiceMultiSelectProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((option) => option.name.toLowerCase().includes(q));
  }, [options, query]);

  const selected = options.filter((option) => selectedIds.includes(option.id));

  function toggle(id: string) {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((value) => value !== id));
      return;
    }
    onChange([...selectedIds, id]);
  }

  return (
    <div ref={rootRef} className="space-y-2">
      <div className="relative">
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen((value) => !value)}
          className="flex h-10 w-full items-center justify-between rounded-md border border-ds-field bg-ds-card px-3 text-sm text-ds-text shadow-sm focus:border-ds-teal focus:outline-none focus:shadow-ring disabled:cursor-not-allowed disabled:bg-ds-disabled"
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span className="truncate text-left text-ds-muted">
            {selectedIds.length === 0
              ? t('patients.punch_cards.services_placeholder', 'Vælg ydelser')
              : t('patients.punch_cards.services_selected_count', {
                  count: selectedIds.length,
                  defaultValue: '{{count}} ydelser valgt',
                })}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 text-ds-muted" />
        </button>

        {open && !disabled && (
          <div className="absolute z-30 mt-1 w-full rounded-md border border-ds-line-strong bg-ds-card p-2 shadow-pop">
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('patients.punch_cards.services_search', 'Søg ydelser')}
              className="mb-2 w-full rounded-md border border-ds-field bg-ds-card px-3 py-1.5 text-sm text-ds-text focus:border-ds-teal focus:outline-none focus:shadow-ring"
              autoFocus
            />
            <ul className="max-h-48 overflow-y-auto" role="listbox" aria-multiselectable="true">
              {filtered.length === 0 ? (
                <li className="px-2 py-2 text-sm text-ds-muted">
                  {t('patients.punch_cards.services_none_found', 'Ingen ydelser matcher')}
                </li>
              ) : (
                filtered.map((option) => {
                  const checked = selectedIds.includes(option.id);
                  return (
                    <li key={option.id}>
                      <label className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-ds-text hover:bg-ds-row-hover">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggle(option.id)}
                          className="h-4 w-4 rounded border-ds-field text-ds-teal focus:ring-ds-teal"
                        />
                        <span className="truncate">{option.name}</span>
                      </label>
                    </li>
                  );
                })
              )}
            </ul>
          </div>
        )}
      </div>

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((option) => (
            <span
              key={option.id}
              className="inline-flex items-center gap-1 rounded-full bg-ds-teal-tint px-2.5 py-0.5 text-xs font-medium text-ds-teal"
            >
              {option.name}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => toggle(option.id)}
                  className="rounded-full p-0.5 hover:bg-ds-teal-line"
                  aria-label={t('patients.punch_cards.services_remove', {
                    name: option.name,
                    defaultValue: 'Fjern {{name}}',
                  })}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
